import React, { useMemo, useState } from 'react';
import { Sparkles, CheckCircle, AlertTriangle, Plug, ChevronRight, LogOut, LogIn, Plus, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MobileLayout } from '../components/MobileLayout';
import { Card, CardContent } from '../components/ui/Card';
import { Tabs } from '../components/ui/Tabs';
import { PianificaPuliziaWizard } from '../components/PianificaPuliziaWizard';
import { useScreenInit } from '../useScreenInit.js';
type CleaningStatus = 'da_pianificare' | 'pianificata' | 'presa_in_carico';
type BookingSource = 'Booking.com' | 'Airbnb';
interface PlannedCleaning {
  id: string;
  date: string;
  dayName: string;
  dayNum: string;
  month: string;
  // Check-out and check-in can come from different channels → separate links.
  checkout: string;
  guestOut: string;
  sourceOut: BookingSource;
  urlOut: string;
  checkin: string;
  guestIn: string;
  sourceIn: BookingSource;
  urlIn: string;
  status: CleaningStatus;
  cleaner?: string;
}
const BK = 'https://www.booking.com';
const AB = 'https://www.airbnb.com';
// House is always the one selected in the sidebar, so it is NOT repeated per card.
const CLEANINGS: PlannedCleaning[] = [{
  id: 'c1',
  date: '2024-01-15',
  dayName: 'Lun',
  dayNum: '15',
  month: 'Gen',
  checkout: '10:00',
  guestOut: 'Roberto F.',
  sourceOut: 'Booking.com',
  urlOut: BK,
  checkin: '15:00',
  guestIn: 'Francesca N.',
  sourceIn: 'Airbnb',
  urlIn: AB,
  status: 'presa_in_carico',
  cleaner: 'Francesca N.'
}, {
  id: 'c2',
  date: '2024-01-16',
  dayName: 'Mar',
  dayNum: '16',
  month: 'Gen',
  checkout: '11:00',
  guestOut: 'Luca P.',
  sourceOut: 'Airbnb',
  urlOut: AB,
  checkin: '16:00',
  guestIn: 'Giulia S.',
  sourceIn: 'Airbnb',
  urlIn: AB,
  status: 'pianificata'
}, {
  id: 'c3',
  date: '2024-01-18',
  dayName: 'Gio',
  dayNum: '18',
  month: 'Gen',
  checkout: '10:00',
  guestOut: 'Francesca N.',
  sourceOut: 'Airbnb',
  urlOut: AB,
  checkin: '14:00',
  guestIn: 'Davide C.',
  sourceIn: 'Booking.com',
  urlIn: BK,
  status: 'da_pianificare'
}, {
  id: 'c4',
  date: '2024-01-19',
  dayName: 'Ven',
  dayNum: '19',
  month: 'Gen',
  checkout: '11:00',
  guestOut: 'Sofia L.',
  sourceOut: 'Booking.com',
  urlOut: BK,
  checkin: '14:30',
  guestIn: 'Paolo V.',
  sourceIn: 'Airbnb',
  urlIn: AB,
  status: 'da_pianificare'
}, {
  id: 'c5',
  date: '2024-01-21',
  dayName: 'Dom',
  dayNum: '21',
  month: 'Gen',
  checkout: '10:00',
  guestOut: 'Marta B.',
  sourceOut: 'Airbnb',
  urlOut: AB,
  checkin: '15:00',
  guestIn: 'Enrico T.',
  sourceIn: 'Booking.com',
  urlIn: BK,
  status: 'pianificata'
}, {
  id: 'c6',
  date: '2024-01-22',
  dayName: 'Lun',
  dayNum: '22',
  month: 'Gen',
  checkout: '11:00',
  guestOut: 'Paolo V.',
  sourceOut: 'Airbnb',
  urlOut: AB,
  checkin: '15:00',
  guestIn: 'Chiara B.',
  sourceIn: 'Booking.com',
  urlIn: BK,
  status: 'presa_in_carico',
  cleaner: 'Roberto F.'
}, {
  id: 'c7',
  date: '2024-01-23',
  dayName: 'Mar',
  dayNum: '23',
  month: 'Gen',
  checkout: '10:00',
  guestOut: 'Davide C.',
  sourceOut: 'Booking.com',
  urlOut: BK,
  checkin: '16:00',
  guestIn: 'Valentina R.',
  sourceIn: 'Booking.com',
  urlIn: BK,
  status: 'da_pianificare'
}, {
  id: 'c8',
  date: '2024-01-25',
  dayName: 'Gio',
  dayNum: '25',
  month: 'Gen',
  checkout: '11:00',
  guestOut: 'Elena T.',
  sourceOut: 'Booking.com',
  urlOut: BK,
  checkin: '14:30',
  guestIn: 'Simone G.',
  sourceIn: 'Airbnb',
  urlIn: AB,
  status: 'pianificata'
}, {
  id: 'c9',
  date: '2024-01-26',
  dayName: 'Ven',
  dayNum: '26',
  month: 'Gen',
  checkout: '10:00',
  guestOut: 'Andrea M.',
  sourceOut: 'Airbnb',
  urlOut: AB,
  checkin: '15:00',
  guestIn: 'Martina L.',
  sourceIn: 'Booking.com',
  urlIn: BK,
  status: 'da_pianificare'
}, {
  id: 'c10',
  date: '2024-01-28',
  dayName: 'Dom',
  dayNum: '28',
  month: 'Gen',
  checkout: '11:00',
  guestOut: 'Chiara B.',
  sourceOut: 'Booking.com',
  urlOut: BK,
  checkin: '16:00',
  guestIn: 'Federico T.',
  sourceIn: 'Airbnb',
  urlIn: AB,
  status: 'da_pianificare'
}, {
  id: 'c11',
  date: '2024-01-30',
  dayName: 'Mar',
  dayNum: '30',
  month: 'Gen',
  checkout: '10:00',
  guestOut: 'Federico T.',
  sourceOut: 'Airbnb',
  urlOut: AB,
  checkin: '15:00',
  guestIn: 'Laura M.',
  sourceIn: 'Booking.com',
  urlIn: BK,
  status: 'pianificata'
}];
const AVAILABLE_CLEANINGS = 13;
const STATUS_META: Record<CleaningStatus, {
  label: string;
  cls: string;
  dot: string;
}> = {
  da_pianificare: {
    label: 'Da pianificare',
    cls: 'bg-coral/10 text-coral border-coral/20',
    dot: 'bg-coral'
  },
  pianificata: {
    label: 'Pianificata',
    cls: 'bg-teal-deep/10 text-teal-deep border-teal-deep/20',
    dot: 'bg-teal-deep'
  },
  presa_in_carico: {
    label: 'Presa in carico',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500'
  }
};
function StatusBadge({
  status
}: {
  status: CleaningStatus;
}) {
  const m = STATUS_META[status];
  return <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${m.cls}`} data-id="element-1266">
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} data-id="element-1267" />
      {m.label}
    </span>;
}
function DateChip({
  c
}: {
  c: PlannedCleaning;
}) {
  return <div className="flex-shrink-0 self-start w-14 text-center border border-border rounded-lg py-2 px-1.5" data-id="element-1268">
      <div className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground" data-id="element-1269">
        {c.dayName}
      </div>
      <div className="text-xl font-bold text-navy leading-tight" data-id="element-1270">
        {c.dayNum}
      </div>
      <div className="text-[11px] text-muted-foreground" data-id="element-1271">{c.month}</div>
    </div>;
}
export function CalendarioPage() {
  useScreenInit();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'da_pianificare' | 'coperte'>('da_pianificare');
  const uncovered = useMemo(() => CLEANINGS.filter(c => c.status === 'da_pianificare'), []);
  const covered = useMemo(() => CLEANINGS.filter(c => c.status !== 'da_pianificare'), []);
  const total = CLEANINGS.length;
  const plannedCount = covered.length;
  const uncoveredCount = uncovered.length;
  const overCapacity = total > AVAILABLE_CLEANINGS;
  const list = activeTab === 'da_pianificare' ? uncovered : covered;
  return <MobileLayout data-id="element-1272">
      {/* Header */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3" data-id="element-1273">
        <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-1274">
          Pianificazione
        </h1>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-white border border-border rounded-full px-3 py-1.5" data-id="element-1275">
          <Plug size={13} className="text-coral" data-id="element-1276" />
          Prenotazioni da Google · Booking
        </span>
      </div>

      {/* Slim summary line */}
      <div className="bg-white border border-border rounded-xl px-4 py-3 mb-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm" data-id="element-1277">
        <span className="inline-flex items-center gap-1.5 text-navy font-semibold" data-id="element-1278">
          <Sparkles size={14} className="text-coral" data-id="element-1279" />
          {total} pulizie
        </span>
        <span className="inline-flex items-center gap-1.5 text-muted-foreground" data-id="element-1280">
          <span className="w-2 h-2 rounded-full bg-teal-deep" data-id="element-1281" />
          {plannedCount} pianificate
        </span>
        <span className="inline-flex items-center gap-1.5 text-muted-foreground" data-id="element-1282">
          <span className="w-2 h-2 rounded-full bg-coral" data-id="element-1283" />
          {uncoveredCount} da pianificare
        </span>
      </div>

      {/* Compact package balance — warning only when demand exceeds balance */}
      {overCapacity ? <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-coral/5 border border-coral/20 rounded-xl px-4 py-2.5 mb-6" data-id="element-1284">
          <p className="text-xs text-coral flex items-start gap-1.5" data-id="element-1285">
            <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" data-id="element-1286" />
            Saldo pacchetto: {AVAILABLE_CLEANINGS} disponibili · la domanda
            supera il saldo.
          </p>
          <Link to="/pacchetti" className="inline-flex items-center justify-center gap-1 text-xs font-semibold text-coral hover:underline flex-shrink-0" data-id="element-1287">
            Ricarica pacchetto
            <ChevronRight size={14} data-id="element-1288" />
          </Link>
        </div> : <div className="mb-6" data-id="element-1289" />}

      {/* Tabs: Da pianificare / Pianificate */}
      <Tabs className="mb-5" value={activeTab} onValueChange={id => setActiveTab(id as 'da_pianificare' | 'coperte')} items={[{
      id: 'da_pianificare',
      label: <>
                Da pianificare{' '}
                <span className="font-semibold opacity-80" data-id="element-1291">
                  {uncoveredCount}
                </span>
              </>
    }, {
      id: 'coperte',
      label: <>
                Pianificate{' '}
                <span className="font-semibold opacity-80" data-id="element-1292">{plannedCount}</span>
              </>
    }]} data-id="element-1290" />

      {/* List */}
      {list.length > 0 ? <div className="space-y-3" data-id="element-1293">
          {list.map((c, i) => <motion.div key={c.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        delay: i * 0.04
      }} data-id="element-1294">
              <Card className="hover:border-foreground/20 hover:shadow-sm transition-all" data-id="element-1295">
                <CardContent className="flex items-start gap-4" data-id="element-1296">
                  <DateChip c={c} data-id="element-1297" />
                  <div className="flex-1 min-w-0" data-id="element-1298">
                    {/* Status tag above the check-out / check-in labels */}
                    <div className="min-w-0" data-id="element-1299">
                      <div className="mb-2" data-id="element-1300">
                        <StatusBadge status={c.status} data-id="element-1301" />
                      </div>
                      <div className="flex items-center justify-between gap-3" data-id="element-1302">
                        <div className="space-y-1 min-w-0 flex-1" data-id="element-1303">
                          {/* Check-out */}
                          <div className="flex items-center gap-1.5 text-sm min-w-0" data-id="element-1304">
                            <LogOut size={14} className="text-coral flex-shrink-0" aria-label="Check-out" data-id="element-1305" />
                            <span className="font-semibold text-navy" data-id="element-1306">
                              {c.checkout}
                            </span>
                            <span className="text-foreground truncate" data-id="element-1307">
                              {c.guestOut}
                            </span>
                            <a href={c.urlOut} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-0.5 text-[11px] font-medium text-navy hover:underline flex-shrink-0" data-id="element-1308">
                              {c.sourceOut}
                              <ExternalLink size={10} className="text-muted-foreground" data-id="element-1309" />
                            </a>
                          </div>
                          {/* Check-in */}
                          <div className="flex items-center gap-1.5 text-sm min-w-0" data-id="element-1310">
                            <LogIn size={14} className="text-teal-deep flex-shrink-0" aria-label="Check-in" data-id="element-1311" />
                            <span className="font-semibold text-navy" data-id="element-1312">
                              {c.checkin}
                            </span>
                            <span className="text-foreground truncate" data-id="element-1313">
                              {c.guestIn}
                            </span>
                            <a href={c.urlIn} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-0.5 text-[11px] font-medium text-navy hover:underline flex-shrink-0" data-id="element-1314">
                              {c.sourceIn}
                              <ExternalLink size={10} className="text-muted-foreground" data-id="element-1315" />
                            </a>
                          </div>
                        </div>
                        {c.status === 'da_pianificare' && <button onClick={() => setWizardOpen(true)} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-xs font-semibold bg-white border border-border text-navy hover:bg-muted transition-colors flex-shrink-0 self-center" data-id="element-1316">
                            <Plus size={14} data-id="element-1317" />
                            Pianifica
                          </button>}
                      </div>
                    </div>

                    {/* LINE 2 — secondary: assigned cleaner OR plan action */}
                    {c.status === 'presa_in_carico' && c.cleaner && <div className="mt-2.5 flex items-center gap-1.5 text-xs text-muted-foreground" data-id="element-1318">
                        <span className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center text-[9px] font-bold" data-id="element-1319">
                          {c.cleaner.split(' ').map(p => p[0]).join('')}
                        </span>
                        {c.cleaner}
                      </div>}
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div> : <Card data-id="element-1320">
          <CardContent className="p-10 text-center" data-id="element-1321">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3" data-id="element-1322">
              <CheckCircle size={22} className="text-emerald-500" data-id="element-1323" />
            </div>
            <p className="text-sm font-medium text-foreground" data-id="element-1324">
              {activeTab === 'da_pianificare' ? 'Tutte le pulizie sono pianificate' : 'Nessuna pulizia pianificata'}
            </p>
            <p className="text-xs text-muted-foreground mt-1" data-id="element-1325">
              {activeTab === 'da_pianificare' ? 'Nessuna pulizia scoperta nei prossimi check-in.' : 'Le pulizie pianificate appariranno qui.'}
            </p>
          </CardContent>
        </Card>}

      {/* Planning wizard */}
      <PianificaPuliziaWizard isOpen={wizardOpen} onClose={() => setWizardOpen(false)} data-id="element-1326" />
    </MobileLayout>;
}